/**
 * Every word the customer pages show: the same states as labels.js, said to somebody who bought
 * an assistant and does not know what a server, a revision or a container is.
 *
 * Two words, kept apart: a *workspace* is an instance, what runs, restarts and has versions; an
 * *assistant* is the agent that lives in it, with its name, picture and personality. Today a
 * workspace holds one assistant; an OpenClaw gateway may hold several.
 *
 * `text(name)` completes the sentence with the name of the workspace. `tone` is a tone of
 * labels.js, for the badge.
 */

/** The displayed state of an instance (backend: DisplayedState), as the customer reads it. */
export const CUSTOMER_STATE = {
  running: {
    label: 'Active',
    tone: 'ok',
    text: (name) => `The workspace ${name} is up and answering.`,
  },
  running_degraded: {
    label: 'Not answering',
    tone: 'warning',
    text: (name) =>
      `The workspace ${name} is on, but cannot answer. If you changed the Telegram bot recently, connect Telegram again; otherwise contact support.`,
  },
  updating: {
    label: 'Saving changes',
    tone: 'progress',
    text: (name) => `Your changes are on their way to the workspace ${name}. It takes a minute.`,
  },
  failed: {
    label: 'Changes not applied',
    tone: 'warning',
    text: (name) => `Your last changes could not be applied. The workspace ${name} keeps working as before. Try again, or contact support if it happens again.`,
  },
  rollback_failed: {
    label: 'Not working',
    tone: 'danger',
    text: (name) => `The workspace ${name} is not working right now. Contact support: this needs us.`,
  },
  stuck: {
    label: 'Taking longer than usual',
    tone: 'warning',
    text: () => 'Your changes have not arrived yet. We have been told and are looking into it.',
  },
  stopped: {
    label: 'Stopped',
    tone: 'danger',
    text: (name) => `The workspace ${name} is stopped. Restart it to bring it back.`,
  },
  server_offline: {
    label: 'Unreachable',
    tone: 'muted',
    text: (name) => `The workspace ${name} cannot be reached right now. Your changes are saved: they will be applied as soon as it is back.`,
  },
  unknown: {
    label: 'Starting',
    tone: 'muted',
    text: (name) => `The workspace ${name} is getting ready.`,
  },
};

export const customerStateOf = (code) => CUSTOMER_STATE[code] ?? CUSTOMER_STATE.unknown;

/**
 * The name to call the assistant of a workspace with one by: its agent name, or a neutral one
 * before it has any, and for a workspace with several.
 */
export const assistantName = (instance) => (instance.assistantNames?.length === 1 ? instance.assistantNames[0] : null) || 'Your assistant';

/**
 * The id of the only assistant of a revision; null for a revision with several, or without one.
 * A workspace with one assistant shows it as the workspace's own, at the addresses of the
 * workspace (/edit, /telegram).
 */
export const onlyAssistant = (revision) => {
  const ids = Object.keys(revision?.personas ?? {});
  return ids.length === 1 ? ids[0] : null;
};

/**
 * The name to call a workspace by: the one its owners chose. The name of the instance names the
 * gateway, an implementation detail customers never see: the backend does not send it to them.
 */
export const workspaceName = (instance) => instance.displayName;

/** The longest name of a workspace (backend: InstanceCommands.MAX_DISPLAY_NAME). */
export const WORKSPACE_NAME_MAX = 60;

/** Where the page of a workspace is, and its pages below it: by its ref, the instance id for a customer. */
export const workspacePath = (ref) => `/workspaces/${encodeURIComponent(ref)}`;

/** Why a member of the customer finds no Edit: only its owners change a workspace (canManage). */
export const ownersOnly = (instance) =>
  `Only the owners of ${instance.customer} change the workspace ${workspaceName(instance)}: ask one of them.`;

/**
 * The three persona files, as the customer edits them (labels.js PERSONA, same keys). `example` is
 * the placeholder of an empty field: what a first sentence could look like, in plain words.
 */
export const CUSTOMER_PERSONA = [
  {
    key: 'soul',
    title: 'Personality',
    description: 'Who your assistant is: its character, its voice, what it cares about. Write it as you would describe a person, in a few sentences.',
    example: 'You are Anna, kind and patient. You speak simply and never in a hurry. You like to help.',
  },
  {
    key: 'agents',
    title: 'How it behaves',
    description: 'The rules it follows: what to do, what never to do, how long its answers are. One rule per line is easiest.',
    example: 'Always answer in Italian.\nKeep answers short.\nNever give medical advice: suggest calling the doctor.',
  },
  {
    key: 'user',
    title: 'About you',
    description: 'What it should know about you and the people who write to it: names, family, work, habits. Only what helps it answer better.',
    example: 'I am Maria, I run a bakery in Bologna. My son Luca also writes to you.',
  },
];

/** The secret of the Telegram bot of an assistant, as image versions declare it (x-assistant-secrets). */
export const TELEGRAM_SECRET = 'TELEGRAM_BOT_TOKEN';

/** The Telegram bot secret of one assistant (backend: assistantSecretName). */
export const assistantTelegramSecret = (id) => `${TELEGRAM_SECRET}__${id.replaceAll('-', '_').toUpperCase()}`;

/** Where the pages of one assistant of a workspace are. */
export const assistantPath = (ref, id) => `${workspacePath(ref)}/assistants/${encodeURIComponent(id)}`;

/**
 * A failed call, said without the backend's own words: those name servers, keys and fields.
 * The status is enough to say what to do.
 */
export function customerError(error) {
  switch (error?.status) {
    case 403:
      return 'This change is not available on your plan: contact support.';
    case 404:
      return 'This workspace does not exist, or is not yours.';
    case 409:
      return 'Your workspace is not ready for this yet. Try again in a few minutes.';
    case 400:
    case 422:
      return 'Something in the form is not right: check the fields and try again.';
    default:
      return 'Something went wrong. Check your connection and try again.';
  }
}
